/**
 * Split Viewer Component
 * Shows two viewers side by side with flexible viewer selection
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useApp } from '../../store/AppContext';
import { MatterportViewer } from './MatterportViewer';
import { ModelViewer } from './ModelViewer';
import { PointCloudViewer } from './PointCloudViewer';
import { GeoTiffViewer } from './GeoTiffViewer';
import { ThermalViewer } from './ThermalViewer';
import './ViewerCommon.css';
import './DualViewer.css';

// Viewer options for dropdown
type ViewerType = 'matterport' | 'model' | 'pointcloud' | 'geotiff' | 'thermal';

interface ViewerOption {
  id: ViewerType;
  label: string;
  icon: React.ReactNode;
}

const VIEWER_OPTIONS: ViewerOption[] = [
  {
    id: 'matterport',
    label: 'Virtual Tour',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: 'model',
    label: 'BIM Model',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.27 6.96L12 12.01l8.73-5.05" />
        <path d="M12 22.08V12" />
      </svg>
    ),
  },
  {
    id: 'pointcloud',
    label: 'Point Cloud',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="7" cy="7" r="2" />
        <circle cx="17" cy="7" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="7" cy="17" r="2" />
        <circle cx="17" cy="17" r="2" />
      </svg>
    ),
  },
  {
    id: 'geotiff',
    label: 'GeoTIFF',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z" />
        <path d="M8 2v16" />
        <path d="M16 6v16" />
      </svg>
    ),
  },
  {
    id: 'thermal',
    label: 'Thermal',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
      </svg>
    ),
  },
];

// Viewer component mapping
const ViewerComponents: Record<ViewerType, React.ComponentType> = {
  matterport: MatterportViewer,
  model: ModelViewer,
  pointcloud: PointCloudViewer,
  geotiff: GeoTiffViewer,
  thermal: ThermalViewer,
};

export function DualViewer() {
  const { toggleDualView } = useApp();
  const [leftViewer, setLeftViewer] = useState<ViewerType>('matterport');
  const [rightViewer, setRightViewer] = useState<ViewerType>('model');
  const [splitPosition, setSplitPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Get viewer components
  const LeftViewerComponent = ViewerComponents[leftViewer];
  const RightViewerComponent = ViewerComponents[rightViewer];

  // Handle split resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitPosition(Math.min(80, Math.max(20, newPosition)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="dual-viewer">
      <div className="dual-viewer__toolbar">
        <span className="dual-viewer__title">Split View</span>
        <div className="dual-viewer__toolbar-actions">
          <button className="viewer__toolbar-btn" onClick={toggleDualView}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Exit Split View
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`dual-viewer__content ${isDragging ? 'dual-viewer__content--dragging' : ''}`}
      >
        {/* Left Panel */}
        <div className="dual-viewer__panel" style={{ width: `${splitPosition}%` }}>
          <div className="dual-viewer__panel-header">
            <select
              className="dual-viewer__select"
              value={leftViewer}
              onChange={(e) => setLeftViewer(e.target.value as ViewerType)}
            >
              {VIEWER_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="dual-viewer__panel-content">
            <LeftViewerComponent />
          </div>
        </div>

        {/* Resizer */}
        <div className="dual-viewer__resizer" onMouseDown={handleMouseDown}>
          <div className="dual-viewer__resizer-handle" />
        </div>

        {/* Right Panel */}
        <div className="dual-viewer__panel" style={{ width: `${100 - splitPosition}%` }}>
          <div className="dual-viewer__panel-header">
            <select
              className="dual-viewer__select"
              value={rightViewer}
              onChange={(e) => setRightViewer(e.target.value as ViewerType)}
            >
              {VIEWER_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="dual-viewer__panel-content">
            <RightViewerComponent />
          </div>
        </div>
      </div>
    </div>
  );
}
